import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { debounce, get } from 'lodash';
import { ApolloProvider, useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import client from '../../lib/apolloClient';
import CurrencyInput from 'react-currency-input-field';
import { useSuccessionTheme } from './SuccessionThemeProvider';
import InfoTooltip from '../common/InfoTooltip';
import { getRevenueLineTerm, getRevenueLineTerminology } from './revenueLineTerminology';
import { useCurrency } from '../../hooks/useCurrency';

const GET_CURRENT_USER = gql`
  query getCurrentUser {
    getCurrentUser {
      id
      fee_based_revenue
      trailer_revenue
      direct_revenue
      transactional_revenue
      gross_expenses
      branch_net_payout
      expense_growth_rate
      use_benchmark_expense_percentage
      benchmark_expenses
      year_1_gross
      year_2_gross
      year_3_gross
      year_4_gross
      year_5_gross
      revenue_growth_rate
      use_revenue_growth_rate
      update_production_data_automatically
      inventory_user {
        id
        is_rjas
      }
      company {
        id
        business_type
        show_pull_latest_revenue_button
        hide_direct_revenue
        allow_user_to_specify_revenue_growth_rate
        employee_based_advisors
        imported_production_data_read_only
        hide_branch_net_payout
        use_enterprise_provided_growth_rate
        feature_types
        revenue_line_terminology
      }
    }
  }
`;

const RECALCULATE_VALUATION = gql`
  mutation RecalculateValuation(
    $branch_net_payout: Float!,
    $fee_based_revenue: Float!,
    $trailer_revenue: Float!,
    $direct_revenue: Float!,
    $transactional_revenue: Float!,
    $gross_expenses: Float!,
    $expense_growth_rate: Float!,
    $revenue_growth_rate: Float!,
    $use_benchmark_expense_percentage: Boolean,
    $use_revenue_growth_rate: Boolean,
    $year_1_gross: Float!,
    $year_2_gross: Float!,
    $year_3_gross: Float!,
    $year_4_gross: Float!,
    $year_5_gross: Float!
  ) {
    recalculateValuation(
      branch_net_payout: $branch_net_payout,
      fee_based_revenue: $fee_based_revenue,
      trailer_revenue: $trailer_revenue,
      direct_revenue: $direct_revenue,
      transactional_revenue: $transactional_revenue,
      gross_expenses: $gross_expenses,
      expense_growth_rate: $expense_growth_rate,
      revenue_growth_rate: $revenue_growth_rate,
      use_benchmark_expense_percentage: $use_benchmark_expense_percentage,
      use_revenue_growth_rate: $use_revenue_growth_rate,
      year_1_gross: $year_1_gross,
      year_2_gross: $year_2_gross,
      year_3_gross: $year_3_gross,
      year_4_gross: $year_4_gross,
      year_5_gross: $year_5_gross
    ) {
      id
      valueOfPractice
      year1Valuation
      year1AnnualRevenue
      year1RecurringRevenue
      year3Valuation
      year3AnnualRevenue
      year3RecurringRevenue
      year5Valuation
      year5AnnualRevenue
      year5RecurringRevenue
      changeInValuation
      changeInValuationPercentage
      percentile
      sumDiscountedCashFlows
      discountedTerminalValue
      year0GridRate
      year0GrossProduction
      year0TargetPracticeNetRevenue
      year0DirectExpenses
      year0OverheadExpenses
      year0Ebit
      year0Taxes
      year0Fcf
      yearlyData {
        year
        targetPracticeNetRevenue
        growthYoy
        directExpenses
        overheadExpenses
        ebit
        taxes
        fcf
        discountRate
        discountedCashFlow
        mtr
      }
      valuation_last_accessed_date
      user {
        benchmark_expenses
        use_benchmark_expense_percentage
      }
    }
  }
`;

// Add loading spinner component
const LoadingSpinner = () => (
  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const ValuationDrawerContent = ({ user, isOpen, onClose, onRecalculate, initialInputs }) => {
  const intl = useIntl();
  // Use succession theme context
  const { theme, isLightTheme, isDarkTheme, getCSSVariable } = useSuccessionTheme();
  const { symbol, format: formatCurrencyHook } = useCurrency();

  // Debug log to see what's in the user prop
  console.log('ValuationDrawer received user prop:', user);
  console.log('ValuationDrawer user.fee_based_revenue:', user && user.fee_based_revenue);
  console.log('ValuationDrawer user.dcf_id:', user && user.dcf_id);
  console.log('ValuationDrawer user keys:', user ? Object.keys(user) : 'no user');
  console.log('ValuationDrawer window location:', window.location.href);
  
  // Initialize with empty state
  const [state, setState] = useState({
    fee_based_revenue: 0,
    trailer_revenue: 0,
    direct_revenue: 0,
    transactional_revenue: 0,
    gross_expenses: 0,
    branch_net_payout: 0,
    expense_growth_rate: 0,
    use_benchmark_expense_percentage: false,
    benchmark_expenses: 0,
    year_1_gross: 0,
    year_2_gross: 0,
    year_3_gross: 0,
    year_4_gross: 0,
    year_5_gross: 0,
    revenue_growth_rate: 0,
    use_revenue_growth_rate: false,
  });

  // Check if we have a dcf_id in the URL or in the user prop
  const urlParams = new URLSearchParams(window.location.search);
  const dcfId = urlParams.get('dcf_id') || (user && user.dcf_id);
  
  // Only fetch current user if we don't have a dcf_id
  const { loading, error, data } = useQuery(GET_CURRENT_USER, {
    fetchPolicy: 'network-only', // Don't use cache, always fetch fresh data
    notifyOnNetworkStatusChange: true,
    skip: !!dcfId, // Skip query if we have a dcf_id
  });

  const [recalculateValuation, { loading: mutationLoading }] = useMutation(RECALCULATE_VALUATION);

  // Update state when initialInputs or user or GraphQL data changes
  useEffect(() => {
    if (initialInputs) {
      setState({
        fee_based_revenue: initialInputs.fee_based_revenue || 0,
        trailer_revenue: initialInputs.trailer_revenue || 0,
        direct_revenue: initialInputs.direct_revenue || 0,
        transactional_revenue: initialInputs.transactional_revenue || 0,
        gross_expenses: initialInputs.gross_expenses || 0,
        branch_net_payout: (initialInputs.branch_net_payout || 0) * 100,
        expense_growth_rate: (initialInputs.expense_growth_rate || 0) * 100,
        use_benchmark_expense_percentage: Boolean(initialInputs.use_benchmark_expense_percentage),
        benchmark_expenses: initialInputs.benchmark_expenses || 0,
        year_1_gross: initialInputs.year_1_gross || 0,
        year_2_gross: initialInputs.year_2_gross || 0,
        year_3_gross: initialInputs.year_3_gross || 0,
        year_4_gross: initialInputs.year_4_gross || 0,
        year_5_gross: initialInputs.year_5_gross || 0,
        revenue_growth_rate: (initialInputs.revenue_growth_rate || 0) * 100,
        use_revenue_growth_rate: Boolean(initialInputs.use_revenue_growth_rate),
      });
    } else if (data && data.getCurrentUser) {
      const currentUser = data.getCurrentUser;
      setState({
        fee_based_revenue: currentUser.fee_based_revenue || 0,
        trailer_revenue: currentUser.trailer_revenue || 0,
        direct_revenue: currentUser.direct_revenue || 0,
        transactional_revenue: currentUser.transactional_revenue || 0,
        gross_expenses: currentUser.gross_expenses || 0,
        branch_net_payout: (currentUser.branch_net_payout || 0) * 100,
        expense_growth_rate: (currentUser.expense_growth_rate || 0) * 100,
        use_benchmark_expense_percentage: currentUser.use_benchmark_expense_percentage || false,
        benchmark_expenses: currentUser.benchmark_expenses || 0,
        year_1_gross: currentUser.year_1_gross || 0,
        year_2_gross: currentUser.year_2_gross || 0,
        year_3_gross: currentUser.year_3_gross || 0,
        year_4_gross: currentUser.year_4_gross || 0,
        year_5_gross: currentUser.year_5_gross || 0,
        revenue_growth_rate: (currentUser.revenue_growth_rate || 0) * 100,
        use_revenue_growth_rate: currentUser.use_revenue_growth_rate || false,
      });
    }
  }, [initialInputs, data]);

  const handleInputChange = (field, value) => {
    setState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggleChange = (field) => {
    setState(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const formatCurrency = (value) => {
    return formatCurrencyHook(value || 0, { maximumFractionDigits: 0 });
  };

  const reset = () => {
    if (user && Object.keys(user).length > 0) {
      setState({
        fee_based_revenue: user.fee_based_revenue || 0,
        trailer_revenue: user.trailer_revenue || 0,
        direct_revenue: user.direct_revenue || 0,
        transactional_revenue: user.transactional_revenue || 0,
        gross_expenses: user.gross_expenses || 0,
        branch_net_payout: (user.branch_net_payout || 0) * 100,
        expense_growth_rate: (user.expense_growth_rate || 0) * 100,
        use_benchmark_expense_percentage: user.use_benchmark_expense_percentage || false,
        benchmark_expenses: user.benchmark_expenses || 0,
        year_1_gross: user.year_1_gross || 0,
        year_2_gross: user.year_2_gross || 0,
        year_3_gross: user.year_3_gross || 0,
        year_4_gross: user.year_4_gross || 0,
        year_5_gross: user.year_5_gross || 0,
        revenue_growth_rate: (user.revenue_growth_rate || 0) * 100,
        use_revenue_growth_rate: user.use_revenue_growth_rate || false,
      });
    } else if (data && data.getCurrentUser) {
      const currentUser = data.getCurrentUser;
      setState({
        fee_based_revenue: currentUser.fee_based_revenue || 0,
        trailer_revenue: currentUser.trailer_revenue || 0,
        direct_revenue: currentUser.direct_revenue || 0,
        transactional_revenue: currentUser.transactional_revenue || 0,
        gross_expenses: currentUser.gross_expenses || 0,
        branch_net_payout: (currentUser.branch_net_payout || 0) * 100,
        expense_growth_rate: (currentUser.expense_growth_rate || 0) * 100,
        use_benchmark_expense_percentage: currentUser.use_benchmark_expense_percentage || false,
        benchmark_expenses: currentUser.benchmark_expenses || 0,
        year_1_gross: currentUser.year_1_gross || 0,
        year_2_gross: currentUser.year_2_gross || 0,
        year_3_gross: currentUser.year_3_gross || 0,
        year_4_gross: currentUser.year_4_gross || 0,
        year_5_gross: currentUser.year_5_gross || 0,
        revenue_growth_rate: (currentUser.revenue_growth_rate || 0) * 100,
        use_revenue_growth_rate: currentUser.use_revenue_growth_rate || false,
      });
    }
  };

  const recalculate = async () => {
    try {
      // Call the GraphQL mutation
      const response = await recalculateValuation({
        variables: {
          branch_net_payout: parseFloat(state.branch_net_payout) / 100,
          fee_based_revenue: parseFloat(state.fee_based_revenue),
          trailer_revenue: parseFloat(state.trailer_revenue),
          direct_revenue: parseFloat(state.direct_revenue),
          transactional_revenue: parseFloat(state.transactional_revenue),
          gross_expenses: parseFloat(state.gross_expenses),
          expense_growth_rate: parseFloat(state.expense_growth_rate) / 100,
          revenue_growth_rate: state.use_revenue_growth_rate ? parseFloat(state.revenue_growth_rate) / 100 : 0.03, // Default to 3% if not using custom rate
          use_benchmark_expense_percentage: Boolean(state.use_benchmark_expense_percentage),
          use_revenue_growth_rate: Boolean(state.use_revenue_growth_rate),
          year_1_gross: parseFloat(state.year_1_gross),
          year_2_gross: parseFloat(state.year_2_gross),
          year_3_gross: parseFloat(state.year_3_gross),
          year_4_gross: parseFloat(state.year_4_gross),
          year_5_gross: parseFloat(state.year_5_gross),
        }
      });

      // Check if response exists and contains data
      if (response && response.data && response.data.recalculateValuation) {
        // Get the updated valuation data from the response
        const valuationData = response.data.recalculateValuation;
        
        // Call the parent component's onRecalculate function with the updated data
        if (onRecalculate) {
          const usedInputs = {
            dcf_id: valuationData.id,  // Use the ID from the recalculated valuation
            branch_net_payout: parseFloat(state.branch_net_payout) / 100,
            fee_based_revenue: parseFloat(state.fee_based_revenue),
            trailer_revenue: parseFloat(state.trailer_revenue),
            direct_revenue: parseFloat(state.direct_revenue),
            transactional_revenue: parseFloat(state.transactional_revenue),
            gross_expenses: parseFloat(state.gross_expenses),
            expense_growth_rate: parseFloat(state.expense_growth_rate) / 100,
            revenue_growth_rate: state.use_revenue_growth_rate ? parseFloat(state.revenue_growth_rate) / 100 : 0.03,
            use_benchmark_expense_percentage: Boolean(state.use_benchmark_expense_percentage),
            benchmark_expenses: parseFloat(state.benchmark_expenses),
            use_revenue_growth_rate: Boolean(state.use_revenue_growth_rate),
            year_1_gross: parseFloat(state.year_1_gross),
            year_2_gross: parseFloat(state.year_2_gross),
            year_3_gross: parseFloat(state.year_3_gross),
            year_4_gross: parseFloat(state.year_4_gross),
            year_5_gross: parseFloat(state.year_5_gross),
            value_of_practice: valuationData.valueOfPractice,  // Include the calculated value
          };
          console.log('ValuationDrawer: calling onRecalculate with usedInputs:', usedInputs);
          onRecalculate(valuationData, usedInputs);
        }
        
        // Close the drawer
        onClose();
      } else {
        console.error('Invalid response structure:', response);
      }
    } catch (err) {
      console.error('Error in recalculation:', err);
      // Display error to the user if needed
    }
  };

  if (!isOpen) return null;

  const hideDirectRevenue = get(data, 'getCurrentUser.company.hide_direct_revenue', false);
  const showRevGrowthRate = get(data, 'getCurrentUser.company.allow_user_to_specify_revenue_growth_rate', false);
  const employeeBasedAdvisors = get(data, 'getCurrentUser.company.employee_based_advisors', false);
  const importedProductionReadOnly = get(data, 'getCurrentUser.company.imported_production_data_read_only', false);
  const useEnterpriseProvidedGrowthRate = get(data, 'getCurrentUser.company.use_enterprise_provided_growth_rate', false);
  const hideBranchNetPayout = get(data, 'getCurrentUser.company.hide_branch_net_payout', false);
  const isRjas = get(data, 'getCurrentUser.inventory_user.is_rjas', false);
  const featureTypes = get(data, 'getCurrentUser.company.feature_types', []);
  const showRjasBenchmarkExpenses = featureTypes?.includes('rjas_benchmark_expenses');
  const disableRevenueInputs = get(data, 'getCurrentUser.update_production_data_automatically', false) || importedProductionReadOnly;
  const disableGrossInputs = disableRevenueInputs || useEnterpriseProvidedGrowthRate;
  const terminology = getRevenueLineTerminology(get(data, 'getCurrentUser.company.revenue_line_terminology', {}));

  // Debug - log current state
  console.log('Current drawer state:', state);
  console.log('company:', data?.getCurrentUser?.company);

  return (
    <div className="fixed inset-0 z-50 flex justify-end overflow-hidden">
      <div className="pointer-events-auto fixed inset-0 bg-black bg-opacity-25" onClick={onClose}></div>
      
      <div className="fixed right-0 top-0 flex h-screen w-screen max-w-md flex-col bg-white shadow-xl">
        <div 
          className="px-4 py-4 sm:px-6 transition-colors duration-300 sticky top-0"
          style={{
            backgroundColor: getCSSVariable('--succession-primary') || theme?.colors?.primary || '#5489B6'
          }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-white transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.drawer_title' })}</h2>
            <button
              type="button"
              className="rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white transition-colors duration-300"
              style={{
                backgroundColor: 'transparent'
              }}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <span className="sr-only">{intl.formatMessage({ id: 'succession.valuation.close_panel' })}</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
          <div className="space-y-6">
            {/* Practice Info Section */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{intl.formatMessage({ id: 'succession.valuation.practice_information' })}</h3>
              
              <div className="space-y-4">
                {!hideBranchNetPayout && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'succession.valuation.branch_net_payout' })}</label>
                    <CurrencyInput
                      id="branch_net_payout"
                      name="branch_net_payout"
                      placeholder="0%"
                      value={state.branch_net_payout}
                      decimalsLimit={2}
                      suffix="%"
                      disabled={employeeBasedAdvisors && !isRjas}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                      onValueChange={(value) => handleInputChange('branch_net_payout', parseFloat(value) || 0)}
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'succession.valuation.expense_growth_rate' })}</label>
                  <CurrencyInput
                    id="expense_growth_rate"
                    name="expense_growth_rate"
                    placeholder="0%"
                    value={state.expense_growth_rate}
                    decimalsLimit={2}
                    suffix="%"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    onValueChange={(value) => handleInputChange('expense_growth_rate', parseFloat(value) || 0)}
                  />
                </div>
                
                {(!employeeBasedAdvisors || (employeeBasedAdvisors && isRjas && showRjasBenchmarkExpenses)) && (
                  <div className="flex items-center">
                    <input
                      id="use_benchmark_expense_percentage"
                      name="use_benchmark_expense_percentage"
                      type="checkbox"
                      checked={state.use_benchmark_expense_percentage}
                      onChange={() => handleToggleChange('use_benchmark_expense_percentage')}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="use_benchmark_expense_percentage" className="ml-2 block text-sm text-gray-700">
                      {intl.formatMessage({ id: 'succession.valuation.use_benchmark_expense_percentage' })}
                    </label>
                  </div>
                )}
                {!state.use_benchmark_expense_percentage && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'succession.valuation.gross_expenses' })}</label>
                    <CurrencyInput
                      id="gross_expenses"
                      name="gross_expenses"
                      placeholder={`${symbol}0`}
                      value={state.gross_expenses}
                      decimalsLimit={2}
                      prefix={symbol}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      onValueChange={(value) => handleInputChange('gross_expenses', parseFloat(value) || 0)}
                    />
                  </div>
                )}
                
                {state.use_benchmark_expense_percentage && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'succession.valuation.benchmark_expenses' })}</label>
                    <CurrencyInput
                      id="benchmark_expenses"
                      name="benchmark_expenses"
                      placeholder={`${symbol}0`}
                      value={state.benchmark_expenses}
                      decimalsLimit={2}
                      prefix={symbol}
                      disabled
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                      onValueChange={(value) => handleInputChange('benchmark_expenses', parseFloat(value) || 0)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Revenue Information Title */}
            <div className="mb-2">
              <h3 className="text-lg font-medium text-gray-900">{intl.formatMessage({ id: 'succession.valuation.revenue_information' })}</h3>
            </div>

            {/* Fee Based Revenue */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                <span className="inline-flex items-center gap-2">
                  <span>{getRevenueLineTerm(terminology, 'wealth', 'fee_based_revenue').label}</span>
                  <InfoTooltip content={getRevenueLineTerm(terminology, 'wealth', 'fee_based_revenue').definition} />
                </span>
              </label>
              <CurrencyInput
                id="fee_based_revenue"
                name="fee_based_revenue"
                placeholder={`${symbol}0`}
                value={state.fee_based_revenue}
                decimalsLimit={2}
                prefix={symbol}
                disabled={disableRevenueInputs}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                onValueChange={(value) => handleInputChange('fee_based_revenue', parseFloat(value) || 0)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                <span className="inline-flex items-center gap-2">
                  <span>{getRevenueLineTerm(terminology, 'wealth', 'trailer_revenue').label}</span>
                  <InfoTooltip content={getRevenueLineTerm(terminology, 'wealth', 'trailer_revenue').definition} />
                </span>
              </label>
              <CurrencyInput
                id="trailer_revenue"
                name="trailer_revenue"
                placeholder={`${symbol}0`}
                value={state.trailer_revenue}
                decimalsLimit={2}
                prefix={symbol}
                disabled={disableRevenueInputs}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                onValueChange={(value) => handleInputChange('trailer_revenue', parseFloat(value) || 0)}
              />
            </div>

            {!hideDirectRevenue && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  <span className="inline-flex items-center gap-2">
                    <span>{getRevenueLineTerm(terminology, 'wealth', 'direct_revenue').label}</span>
                    <InfoTooltip content={getRevenueLineTerm(terminology, 'wealth', 'direct_revenue').definition} />
                  </span>
                </label>
                <CurrencyInput
                  id="direct_revenue"
                  name="direct_revenue"
                  placeholder={`${symbol}0`}
                  value={state.direct_revenue}
                  decimalsLimit={2}
                  prefix={symbol}
                  disabled={disableRevenueInputs}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                  onValueChange={(value) => handleInputChange('direct_revenue', parseFloat(value) || 0)}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                <span className="inline-flex items-center gap-2">
                  <span>{getRevenueLineTerm(terminology, 'wealth', 'transactional_revenue').label}</span>
                  <InfoTooltip content={getRevenueLineTerm(terminology, 'wealth', 'transactional_revenue').definition} />
                </span>
              </label>
              <CurrencyInput
                id="transactional_revenue"
                name="transactional_revenue"
                placeholder={`${symbol}0`}
                value={state.transactional_revenue}
                decimalsLimit={2}
                prefix={symbol}
                disabled={disableRevenueInputs}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                onValueChange={(value) => handleInputChange('transactional_revenue', parseFloat(value) || 0)}
              />
            </div>

            

            
            
            {/* Gross Production Section */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{intl.formatMessage({ id: 'succession.valuation.gross_production_section' })}</h3>
              
              {showRevGrowthRate && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'succession.valuation.revenue_growth_rate' })}</label>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-2">{intl.formatMessage({ id: 'succession.valuation.specify_revenue_growth_rate' })}</span>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          type="checkbox"
                          id="use_revenue_growth_rate"
                          checked={state.use_revenue_growth_rate}
                          onChange={() => handleToggleChange('use_revenue_growth_rate')}
                          disabled={useEnterpriseProvidedGrowthRate}
                          className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                        />
                        <label
                          htmlFor="use_revenue_growth_rate"
                          className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                        ></label>
                      </div>
                    </div>
                  </div>
                  
                  {state.use_revenue_growth_rate && (
                    <div className="mt-1">
                      <CurrencyInput
                        id="revenue_growth_rate"
                        name="revenue_growth_rate"
                        placeholder="0%"
                        value={state.revenue_growth_rate}
                        decimalsLimit={2}
                        suffix="%"
                        disabled={useEnterpriseProvidedGrowthRate}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                        onValueChange={(value) => {
                          const numericValue = parseFloat(value) || 0;
                          handleInputChange('revenue_growth_rate', numericValue);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
              
              {!state.use_revenue_growth_rate && (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((year) => (
                    <div key={year}>
                      <label className="block text-sm font-medium text-gray-700">{new Date().getFullYear() - year}</label>
                      <CurrencyInput
                        id={`year_${year}_gross`}
                        name={`year_${year}_gross`}
                        placeholder={`${symbol}0`}
                        value={state[`year_${year}_gross`]}
                        decimalsLimit={2}
                        prefix={symbol}
                        disabled={disableGrossInputs}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                        onValueChange={(value) => handleInputChange(`year_${year}_gross`, parseFloat(value) || 0)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-5 sm:px-6">
          <div className="flex justify-between space-x-3">
            <button
              type="button"
              disabled={mutationLoading}
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={reset}
            >
              {intl.formatMessage({ id: 'succession.valuation.reset' })}
            </button>
            <button
              type="button"
              disabled={mutationLoading}
              onClick={recalculate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-succession-primary hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-succession-primary disabled:opacity-50 transition-colors duration-300"
            >
              {mutationLoading ? (
                <>
                  <LoadingSpinner />
                  {intl.formatMessage({ id: 'succession.valuation.calculating' })}
                </>
              ) : (
                intl.formatMessage({ id: 'succession.valuation.recalculate' })
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrap the component with ApolloProvider
const ValuationDrawer = (props) => {
  // Debug log the entire props object
  console.log('ValuationDrawer props:', props);
  
  if (!props.isOpen) return null;
  
  return (
    <ApolloProvider client={client}>
      <ValuationDrawerContent {...props} />
    </ApolloProvider>
  );
};

ValuationDrawer.propTypes = {
  user: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRecalculate: PropTypes.func,
};

ValuationDrawer.defaultProps = {
  user: {},
  onRecalculate: null,
};

const mapStateToProps = (state, ownProps) => {
  // If we have a user prop passed directly (with DCF data), use it
  // Otherwise fall back to Redux state.user
  return {
    user: ownProps.user || state.user || {},
  };
};

export default connect(mapStateToProps)(ValuationDrawer); 